import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  DashboardApplicationListResponse,
  DashboardApplicationStatusFilter,
  DashboardApplicationTypeFilter,
  DashboardBriefApplicationDetail,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

export const APPLICATIONS_API_PATH = '/dashboard/applications';

export interface ApplicationFilters {
  status: DashboardApplicationStatusFilter;
  type: DashboardApplicationTypeFilter;
  /** Briefs and programs picked in the "Brief ou programme" filter. */
  targetIds?: string[];
  search?: string;
  sortDirection: 'asc' | 'desc';
  page: number;
  perPage: number;
}

/** Every applications read hangs under one root, so a decision refreshes them all. */
export const applicationKeys = {
  all: ['dashboard-applications'] as const,
  list: (brandId: string | null, filters: ApplicationFilters, sourceScope = 'unspecified') =>
    [...applicationKeys.all, 'list', brandId, filters, sourceScope] as const,
  briefDetail: (brandId: string | null, applicationId: string) =>
    [...applicationKeys.all, 'brief', brandId, applicationId] as const,
  pendingCount: (brandId: string | null, sourceScope = 'unspecified') =>
    [...applicationKeys.all, 'pending-count', brandId, sourceScope] as const,
};

/** Which sources the reader may see: a flag flip must not reuse the other scope's cache. */
export interface ApplicationSourceAccess {
  briefs: boolean;
}

const applicationSourceScope = (access: ApplicationSourceAccess): string =>
  String(Number(access.briefs));

const fetchApplications = async (
  filters: Partial<ApplicationFilters>,
  signal?: AbortSignal
): Promise<DashboardApplicationListResponse> => {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.targetIds?.length) params.set('targetIds', filters.targetIds.join(','));
  if (filters.search) params.set('search', filters.search);
  if (filters.sortDirection) params.set('sortDirection', filters.sortDirection);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.perPage) params.set('perPage', String(filters.perPage));

  const { data } = await api.get<ApiResponse<DashboardApplicationListResponse>>(
    `${APPLICATIONS_API_PATH}?${params.toString()}`,
    { signal }
  );
  if (!data.success || !data.data) {
    throw new Error(data.message || 'Failed to fetch applications');
  }
  return data.data;
};

/** The applications page: briefs and programs in one list. */
export function useApplicationsQuery({
  brandId,
  filters,
  enabled = true,
  sourceAccess,
}: {
  brandId: string | null;
  filters: ApplicationFilters;
  enabled?: boolean;
  sourceAccess?: ApplicationSourceAccess;
}) {
  const sourceScope = sourceAccess ? applicationSourceScope(sourceAccess) : 'unspecified';
  return useQuery<DashboardApplicationListResponse, Error>({
    queryKey: applicationKeys.list(brandId, filters, sourceScope),
    queryFn: ({ signal }) => fetchApplications(filters, signal),
    enabled: enabled && Boolean(brandId),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === brandId && previousQuery.queryKey[4] === sourceScope
        ? previousData
        : undefined,
    staleTime: 30_000,
  });
}

/**
 * One brief application, for a deep link (`?c=`) naming one that is not on the
 * page being read. Program applications are only ever read from the list.
 */
export function useBriefApplicationQuery(
  brandId: string | null,
  applicationId: string | null,
  enabled = true
) {
  return useQuery<DashboardBriefApplicationDetail, Error>({
    queryKey: applicationKeys.briefDetail(brandId, applicationId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<DashboardBriefApplicationDetail>>(
        `${APPLICATIONS_API_PATH}/briefs/${applicationId}`
      );
      if (!data.success || !data.data) {
        throw new Error(data.message || 'Failed to fetch the application');
      }
      return data.data;
    },
    enabled: enabled && Boolean(brandId) && Boolean(applicationId),
    retry: false,
  });
}

/** Pending applications across both sources, whatever the filters: the menu badge. */
export function useApplicationsPendingCountQuery(
  brandId: string | null | undefined,
  enabled: boolean,
  sourceAccess?: ApplicationSourceAccess
) {
  const sourceScope = !enabled
    ? 'disabled'
    : sourceAccess
      ? applicationSourceScope(sourceAccess)
      : 'unspecified';
  return useQuery<number, Error>({
    queryKey: applicationKeys.pendingCount(brandId ?? null, sourceScope),
    queryFn: async ({ signal }) =>
      (await fetchApplications({ status: 'pending', perPage: 1 }, signal)).pendingCount,
    enabled: enabled && Boolean(brandId),
    staleTime: 60_000,
  });
}
